import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

const WEB_POPUP_IMAGE_URL =
  "https://api.learning2earning.net/Image/Website/1/Popup/WebPopup.png";

function StatCounter({ target, suffix = "", duration = 1200 }) {
  const [value, setValue] = useState(0);
  const elRef = useRef(null);
  const animatedRef = useRef(false);

  useEffect(() => {
    const el = elRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !animatedRef.current) {
            animatedRef.current = true;
            let start = null;
            function step(ts) {
              if (!start) start = ts;
              const progress = Math.min((ts - start) / duration, 1);
              const val = target * progress;
              setValue(
                target % 1 === 0 ? Math.floor(val) : Number(val.toFixed(1)),
              );
              if (progress < 1) requestAnimationFrame(step);
            }
            requestAnimationFrame(step);
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.4 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [target, duration]);

  return (
    <strong ref={elRef}>
      {value}
      {suffix}
    </strong>
  );
}

export default function Home() {
  window.scrollTo({ top: 0, behavior: "smooth" });

  const [popupImageUrl, setPopupImageUrl] = useState(null);

  useEffect(() => {
    const img = new Image();
    img.onload = () => setPopupImageUrl(WEB_POPUP_IMAGE_URL);
    img.onerror = () => setPopupImageUrl(null);
    img.src = WEB_POPUP_IMAGE_URL;
  }, []);

  return (
    <>
      {popupImageUrl && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(6,8,12,0.72)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            padding: 16,
          }}
          onClick={() => setPopupImageUrl(null)}
        >
          <div
            style={{ position: "relative", maxWidth: 480, width: "100%" }}
            onClick={(e) => e.stopPropagation()}
          >
            <span
              onClick={() => setPopupImageUrl(null)}
              style={{
                position: "absolute",
                top: -14,
                right: -14,
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: "#fff",
                color: "#111",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 20,
                fontWeight: 700,
                cursor: "pointer",
                boxShadow: "0 6px 18px rgba(0,0,0,0.4)",
              }}
            >
              &times;
            </span>
            <img
              src={popupImageUrl}
              alt="Announcement"
              style={{
                width: "100%",
                height: "auto",
                borderRadius: 14,
                display: "block",
                boxShadow: "0 40px 100px -30px rgba(0,0,0,0.7)",
              }}
            />
          </div>
        </div>
      )}
      <>
        <section className="hero">
          <div className="dot-grid-layer" />
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-6">
                <span className="kicker">
                  WELCOME TO THE LEARNING TO EARNING ACADEMY
                </span>
                <h1 className="mt-3">
                  Learn how markets <em>actually</em> move.
                </h1>
                <p className="lead mt-3">
                  Learning 2 Earning Academy trains beginners and working
                  professionals to read charts, manage risk and trade with
                  discipline — not guesswork. 10+ years of classroom experience,
                  one SEBI-Certified research analyst on faculty, and a
                  community that keeps showing up.
                </p>
                <div className="d-flex flex-wrap mt-4" style={{ gap: ".9rem" }}>
                  <a
                    href="https://wa.me/7004654910?text=Hi%2C%20I%20saw%20your%20website%2C%20I%27m%20interested%20in%20your%20courses.%20Could%20you%20share%20more%20details%3F"
                    className="btn btn-brass btn-lg"
                    target="_blank"
                    rel="noopener"
                  >
                    Start Learning Now
                  </a>
                  <Link
                    to="/all_courses"
                    className="btn btn-outline-ink btn-lg"
                    style={{
                      borderColor: "rgba(255,255,255,.35)",
                      color: "#fff",
                    }}
                  >
                    Explore Courses
                  </Link>
                </div>
                <div className="hero-stats">
                  <div>
                    <StatCounter target={8559} suffix="+" />
                    <span>Students Trained</span>
                  </div>
                  <div>
                    <StatCounter target={19} suffix="K+" />
                    <span>Teaching Hours</span>
                  </div>
                  <div>
                    <StatCounter target={859} suffix="+" />
                    <span>Verified Reviews</span>
                  </div>
                  <div>
                    <StatCounter target={4.9} />
                    <span>Google Rating</span>
                  </div>
                </div>
              </div>
              <div className="col-lg-6 mt-5 mt-lg-0 position-relative">
                <div
                  className="float-badge"
                  style={{ top: "-18px", right: 10 }}
                >
                  <div className="fb-icon">
                    <i className="fa-solid fa-trophy" />
                  </div>
                  <div>
                    <strong>Most Trusted</strong>
                    <span>Learning 2 Earning Institute 2026</span>
                  </div>
                </div>
                <div className="hero-img-frame">
                  <img
                    src="assets/website/img/learning-4-top.jpg"
                    alt="Students learning trading at Learning 2 Earning Academy"
                    loading="lazy"
                  />
                </div>
                <div
                  className="float-badge"
                  style={{ bottom: "-22px", left: "-10px" }}
                >
                  <div
                    className="fb-icon"
                    style={{
                      background: "linear-gradient(135deg,#F59E0B,#f97316)",
                    }}
                  >
                    <i className="fa-solid fa-user-group" />
                  </div>
                  <div>
                    <strong>8,559+ Students</strong>
                    <span>Trained across 5 cities</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="section-sm bg-paper">
          <div className="container">
            <div className="row text-center">
              <div className="col-md-4 mb-3">
                <h5 className="mb-1">10+ Years of Classroom Experience</h5>
                <p className="text-muted-soft small mb-0">
                  A decade of structured teaching combining theory with hands-on
                  chart practice.
                </p>
              </div>
              <div className="col-md-4 mb-3">
                <h5 className="mb-1">4.9 Rating, Thousands of Learners</h5>
                <p className="text-muted-soft small mb-0">
                  Trusted across India for clarity, transparency and a practical
                  approach.
                </p>
              </div>
              <div className="col-md-4 mb-3">
                <h5 className="mb-1">95% Engagement, Personalised Mentoring</h5>
                <p className="text-muted-soft small mb-0">
                  Small batches keep learners motivated all the way to the
                  finish line.
                </p>
              </div>
            </div>
          </div>
        </section>
        <section className="section">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-5">
                <span className="kicker">June 2, 2026</span>
                <h2 className="mt-3">
                  Recognised as India's Most Trusted Stock Market Institute
                </h2>
                <p className="text-muted-soft">
                  Learning 2 earning Academy is set to be honoured with the most
                  trusted stock market academy very soon.
                </p>
                <div className="candle-rule">
                  <i style={{ height: "60%" }} />
                  <i style={{ height: "100%" }} />
                  <i style={{ height: "40%" }} />
                  <i style={{ height: "80%" }} />
                  <i style={{ height: "55%" }} />
                </div>
              </div>
              <div className="col-lg-7 mt-4 mt-lg-0">
                <div className="row">
                  <div className="col-12 mb-3">
                    <img
                      src="assets/website/img/about-1.png"
                      className="img-fluid rounded"
                      alt="Award ceremony with former CM of Uttarakhand"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="section bg-paper">
          <div className="container">
            <div className="row">
              <div className="col-lg-6 mb-4">
                <span className="kicker">Why we exist</span>
                <h3 className="mt-2">Practical learning over prediction</h3>
                <p className="text-muted-soft">
                  Our focus is education, awareness and skill-building. With 10+
                  years of expertise, we prepare learners to understand
                  financial markets with confidence — through market analysis,
                  trading psychology and risk management, so you become an
                  independent decision-maker.
                </p>
              </div>
              <div className="col-lg-6 mb-4">
                <span className="kicker">What keeps students here</span>
                <h3 className="mt-2">
                  We don't just teach — we build habits that stick
                </h3>
                <p className="text-muted-soft">
                  Over 95% of our learners complete their programs — not because
                  they have to, but because the structure keeps them moving
                  forward, one module at a time.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="section bg-paper">
          <div className="container">
            <div className="section-head mx-auto text-center">
              <span className="kicker justify-content-center">
                In their words
              </span>
              <h2>Student testimonials</h2>
            </div>
            <div className="row">
              <div className="col-md-4 mb-4">
                <div className="testi-card">
                  <span className="quote-mark">"</span>
                  <p>
                    With the help of L2E and my mentor I gained a much clearer
                    understanding of the financial market. I'd recommend this to
                    my friends and family.
                  </p>
                  <div className="testi-name">BAJRANGI KUMAR</div>
                  <div className="testi-role">Student</div>
                </div>
              </div>
              <div className="col-md-4 mb-4">
                <div className="testi-card">
                  <span className="quote-mark">"</span>
                  <p>
                    Jab join kiya tha mujhe kuch pata nahi tha — now I know how
                    to follow charts and read the market properly.
                  </p>
                  <div className="testi-name">SUPRIYA</div>
                  <div className="testi-role">House wife</div>
                </div>
              </div>
              <div className="col-md-4 mb-4">
                <div className="testi-card">
                  <span className="quote-mark">"</span>
                  <p>
                    SMC, liquidity and trapping felt heavy at first. With time
                    it turned out to be far easier than expected, and now we're
                    doing really well with it.
                  </p>
                  <div className="testi-name">RAVI</div>
                  <div className="testi-role">Business man</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="section bg-paper">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-4 text-center mb-4 mb-lg-0">
                <img
                  src="assets/website/img/learning_award.jpg"
                  className="img-fluid rounded"
                  alt="Founders of L2E with former CM"
                />
              </div>
              <div className="col-lg-8">
                <span className="kicker">From the founders</span>
                <h2 className="mt-2">
                  When you learn investment, mutual fund ,,, Stock Market and
                  Forex trading properly and practice them, earning good money
                  in life becomes very achievable.
                </h2>
                <p className="text-muted-soft">
                  With discipline , Proper Risk management and the right
                  knowledge, anyone can attain financial freedom. I and the
                  entire team are committed to your success.
                </p>
                <Link to="/about" className="btn btn-ink">
                  Let’s get started today!
                </Link>
              </div>
            </div>
          </div>
        </section>
        <section className="section">
          <div className="container">
            <div className="cta-band text-center">
              <h2>Ready to start your trading journey?</h2>

              <Link
                to="/panel/login"
                className="btn btn-brass btn-lg btn-lg-879"
                rel="noopener"
              >
                Sign up Today
              </Link>
            </div>
          </div>
        </section>
      </>
    </>
  );
}
